export const DEFAULT_GST_RATE = 0.10
export const DEFAULT_STANDARD_MARGIN_PCT = 30

export const PRICE_METHODS = {
  STANDARD_MARKUP: 'standard_markup',
  CUSTOM_MARKUP: 'custom_markup',
  STANDARD_PRICE: 'standard_price'
}

export const PRICE_METHOD_OPTIONS = [
  { id: 'standard_markup', name: 'Cost + Standard Markup' },
  { id: 'custom_markup', name: 'Custom Markup' },
  { id: 'standard_price', name: 'Standard price' }
]

export const INCLUDES_GST_OPTIONS = [
  { id: 1, name: 'Yes' },
  { id: 0, name: 'No' }
]

export function roundMoney (value) {
  const n = parseFloat(value)
  if (!Number.isFinite(n)) return 0
  return parseFloat(n.toFixed(2))
}

export function parseMarginPct (value, fallback = DEFAULT_STANDARD_MARGIN_PCT) {
  const n = parseFloat(value)
  if (!Number.isFinite(n) || n < 0) return fallback
  if (n >= 100) return 99.99
  return n
}

/** Markup on cost: sell = cost × (1 + markup%/100) */
export function sellFromCost (cost, marginPct) {
  const c = parseFloat(cost)
  const pct = parseMarginPct(marginPct)
  if (!Number.isFinite(c) || c <= 0) return 0
  return roundMoney(c * (1 + pct / 100))
}

/** Legacy data stored sell as cost + 10% GST — not a true fixed or custom markup price. */
export function isGstOnlySell (cost, sellPrice) {
  const c = parseFloat(cost)
  const s = parseFloat(sellPrice)
  if (!Number.isFinite(c) || c <= 0 || !Number.isFinite(s) || s <= 0) return false
  return Math.abs(s - roundMoney(c * (1 + DEFAULT_GST_RATE))) < 0.02
}

function isLegacyGstMarkupPct (markupPct) {
  const n = parseFloat(markupPct)
  return Number.isFinite(n) && Math.abs(n - DEFAULT_GST_RATE * 100) < 0.05
}

/** Resolve how to price a line; ignores stale GST-only sells and mistaken 10% custom markups. */
export function effectivePriceMethod (material, standardMarginPct = DEFAULT_STANDARD_MARGIN_PCT) {
  if (!material || typeof material !== 'object') return PRICE_METHODS.STANDARD_MARKUP
  const raw = material.price_method || PRICE_METHODS.STANDARD_MARKUP
  const cost = parseFloat(material.cost)
  const sell = parseFloat(material.sell_price)
  if (raw === PRICE_METHODS.CUSTOM_MARKUP &&
      material.markup_pct != null &&
      String(material.markup_pct).trim() !== '' &&
      !isLegacyGstMarkupPct(material.markup_pct)) {
    return PRICE_METHODS.CUSTOM_MARKUP
  }
  if (raw === PRICE_METHODS.STANDARD_PRICE &&
      Number.isFinite(cost) && cost > 0 &&
      Number.isFinite(sell) && sell > 0 &&
      !isGstOnlySell(cost, sell)) {
    const markupSell = sellFromCost(cost, standardMarginPct)
    if (Math.abs(sell - markupSell) >= 0.02) {
      return PRICE_METHODS.STANDARD_PRICE
    }
  }
  return PRICE_METHODS.STANDARD_MARKUP
}

export function resolveSellPrice ({
  cost,
  price_method: priceMethod = PRICE_METHODS.STANDARD_MARKUP,
  markup_pct: markupPct,
  standard_margin_pct: standardMarginPct = DEFAULT_STANDARD_MARGIN_PCT,
  sell_price: sellPrice
}) {
  const method = effectivePriceMethod({
    cost,
    price_method: priceMethod,
    markup_pct: markupPct,
    sell_price: sellPrice
  }, standardMarginPct)
  if (method === PRICE_METHODS.STANDARD_PRICE) {
    return roundMoney(sellPrice)
  }
  const pct = method === PRICE_METHODS.CUSTOM_MARKUP
    ? parseMarginPct(markupPct, standardMarginPct)
    : parseMarginPct(standardMarginPct)
  return sellFromCost(cost, pct)
}

/** Account markup % to display for a catalog/calculation line. */
export function accountMarkupPctForItem (item, standardMarginPct = DEFAULT_STANDARD_MARGIN_PCT) {
  if (!item) return parseMarginPct(standardMarginPct)
  const method = effectivePriceMethod(item, standardMarginPct)
  if (method === PRICE_METHODS.CUSTOM_MARKUP) {
    return parseMarginPct(item.markup_pct, standardMarginPct)
  }
  return parseMarginPct(standardMarginPct)
}

/** Recompute sell for markup-based price methods using the viewer's account markup %. */
export function applyResolvedSellToMaterial (material, standardMarginPct) {
  if (!material || typeof material !== 'object') return material
  const method = effectivePriceMethod(material, standardMarginPct)
  if (method === PRICE_METHODS.STANDARD_PRICE) {
    return material
  }
  const sell = resolveSellPrice({
    cost: material.cost,
    price_method: method,
    markup_pct: material.markup_pct,
    standard_margin_pct: standardMarginPct,
    sell_price: material.sell_price
  })
  return Object.assign({}, material, {
    sell_price: sell,
    price_method: method === PRICE_METHODS.CUSTOM_MARKUP ? PRICE_METHODS.CUSTOM_MARKUP : PRICE_METHODS.STANDARD_MARKUP
  })
}

/** Numeric materials.id from a catalog or calculation line (strips ,default / ,company suffix). */
export function catalogMaterialId (material) {
  if (!material || material.id == null) return null
  return String(material.id).split(',')[0]
}

/** Overlay fresh catalog/DB pricing onto a saved calculation line; preserves qty, time, etc. */
export function mergeCatalogPricingOntoLineItem (lineItem, catalogItem) {
  if (!lineItem || typeof lineItem !== 'object') return lineItem
  if (!catalogItem || typeof catalogItem !== 'object') return lineItem
  const merged = Object.assign({}, lineItem)
  if (catalogItem.cost != null) merged.cost = catalogItem.cost
  if (catalogItem.price_method) merged.price_method = catalogItem.price_method
  if (catalogItem.markup_pct != null) merged.markup_pct = catalogItem.markup_pct
  if (catalogItem.includes_gst != null) merged.includes_gst = catalogItem.includes_gst
  return merged
}

/** Resolved sell for a catalog line (pipe/fitting/fixing) using account markup. */
export function resolvedCatalogItemSell (item, standardMarginPct = DEFAULT_STANDARD_MARGIN_PCT) {
  if (!item) return 0
  const method = effectivePriceMethod(item, standardMarginPct)
  return resolveSellPrice({
    cost: item.cost,
    price_method: method,
    markup_pct: item.markup_pct,
    standard_margin_pct: standardMarginPct,
    sell_price: item.sell_price
  })
}

export function includesGstFlag (value) {
  if (value === false || value === 0 || value === '0') return false
  if (value === 'no' || value === 'No') return false
  return true
}

/** Convert stored sell to GST-inclusive amount for quote line math */
export function sellPriceForQuote (sellPrice, includesGst) {
  const n = parseFloat(sellPrice)
  if (!Number.isFinite(n)) return 0
  if (includesGstFlag(includesGst)) return roundMoney(n)
  return roundMoney(n * (1 + DEFAULT_GST_RATE))
}

/** GST extracted from a GST-inclusive line amount */
export function gstFromIncGstAmount (amountIncGst) {
  const n = parseFloat(amountIncGst)
  if (!Number.isFinite(n) || n <= 0) return 0
  return roundMoney(n / (1 + DEFAULT_GST_RATE) * DEFAULT_GST_RATE)
}
